import { useEffect, useState } from "react";
import api from "@/lib/api";

const communityImage = "https://images.pexels.com/photos/6129441/pexels-photo-6129441.jpeg";

export default function About() {
  const [board, setBoard] = useState([]);

  useEffect(() => {
    api.get("/board").then((r) => setBoard(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="overline">About AzSAM</div>
          <h1 className="mt-6 font-heading text-4xl sm:text-5xl leading-tight">
            A community grown from Arizona's own addiction specialists.
          </h1>
          <p className="mt-6 text-lg text-[color:var(--azsam-ink-soft)] leading-relaxed">
            Founded in 2006, the Arizona Society of Addiction Medicine is the state chapter of the
            American Society of Addiction Medicine. We exist to elevate the practice of addiction
            medicine — clinically, ethically, and politically — in every corner of Arizona.
          </p>
        </div>
        <div className="lg:col-span-6">
          <img src={communityImage} alt="Clinician meeting with patient" className="w-full h-[440px] object-cover rounded-2xl" data-testid="about-hero-image" />
        </div>
      </section>

      <section className="bg-[color:var(--azsam-cream)] border-y border-[color:var(--azsam-stone)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-2 gap-14">
          <div>
            <div className="overline">Our mission</div>
            <h2 className="mt-4 font-heading text-3xl leading-snug">
              Advance the availability, accessibility, and quality of evidence-based addiction care for every Arizonan.
            </h2>
          </div>
          <div>
            <div className="overline">Our values</div>
            <ul className="mt-4 space-y-4 text-[color:var(--azsam-ink-soft)]">
              <Value title="Science + Compassion">Evidence-based care and human dignity are inseparable.</Value>
              <Value title="Access for All">Every Arizonan deserves access to high-quality addiction care and recovery support.</Value>
              <Value title="Collaboration Over Silos">We bring together healthcare, behavioral health, public health, and community partners to advance solutions.</Value>
              <Value title="Advancing Addiction Medicine">We lead through education, advocacy, innovation, and the continuous pursuit of excellence in addiction care.</Value>
            </ul>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="max-w-2xl">
          <div className="overline">Board of Directors</div>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl">Leadership 2026</h2>
          <p className="mt-4 text-[color:var(--azsam-ink-soft)]">
            Our board is elected annually by AzSAM members and reflects the geographic, disciplinary,
            and career-stage diversity of Arizona's addiction medicine community.
          </p>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="about-board-grid">
          {board.map((m) => (
            <div key={m.id} className="azsam-card p-7" data-testid={`board-${m.id}`}>
              {m.image_url ? (
                <img
                  src={m.image_url}
                  alt={m.name}
                  className="w-24 h-24 rounded-full object-cover ring-2 ring-[color:var(--azsam-clay)]/20"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-[color:var(--azsam-sage)]/15 text-[color:var(--azsam-sage-hover)] font-heading text-xl flex items-center justify-center">
                  {m.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </div>
              )}
              <div className="mt-5 overline text-[color:var(--azsam-sage-hover)]">{m.title}</div>
              <h3 className="mt-2 font-heading text-xl">{m.name}</h3>
              <div className="text-xs text-[color:var(--azsam-ink-soft)] mt-1">{m.credentials}</div>
              <p className="mt-4 text-sm text-[color:var(--azsam-ink-soft)] leading-relaxed">{m.bio}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Value({ title, children }) {
  return (
    <li>
      <div className="font-heading text-xl text-[color:var(--azsam-ink)]">{title}</div>
      <div className="mt-1 text-sm leading-relaxed">{children}</div>
    </li>
  );
}
